// Gaveta / drawer do painel (DESIGN §5.21): formulário lateral no lg; tela cheia no celular.
// Fechar com alterações não salvas pede confirmação ("Descartar alterações?").
import { useId, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { cn } from "../lib/cn";
import { useFocoPreso, useTravarRolagem } from "../lib/hooks";
import { Icones } from "./icones";
import { Confirmar } from "./Modal";

export interface PropsGaveta {
  aberto: boolean;
  aoFechar: () => void;
  titulo: ReactNode;
  /** Rodapé fixo (Cancelar secundário + Salvar tinta/primário). */
  rodape?: ReactNode;
  /**
   * Linha curta logo acima do rodapé, alinhada à direita (por que a ação está desativada: "Escolha um treino ou
   * comece em branco."; mockup Montar ficha, 30/09).
   */
  avisoRodape?: ReactNode;
  /** Pede confirmação antes de fechar. */
  alterado?: boolean;
  /** "larga" = 520 px (leitura, ex.: "Ver em lista" do Treinando agora). Padrão 480. */
  largura?: "padrao" | "larga";
  /** Corpo sem padding nem espaçamento (linhas de borda a borda). */
  semPadding?: boolean;
  /**
   * Abaixo do `lg` (tela cheia): cabeçalho de 64 com o fechar de 48 e rodapé com 12 em cima (mockup
   * painel-video-exercicio §5, gaveta do exercício a 390/360). Padrão: 72, como nas outras pranchas.
   */
  compactaNoCelular?: boolean;
  /**
   * Abaixo do `lg` (tela cheia): cabeçalho de 56 com o fechar à esquerda do título, corpo com 20 de respiro e 16 entre
   * os blocos e rodapé com 12 em cima (mockup personais-agregadores §1.3, gaveta "Planos especiais" a 390).
   */
  fecharAEsquerdaNoCelular?: boolean;
  children: ReactNode;
  className?: string;
}

export function Gaveta({
  aberto,
  aoFechar,
  titulo,
  rodape,
  avisoRodape,
  alterado,
  largura = "padrao",
  semPadding,
  compactaNoCelular,
  fecharAEsquerdaNoCelular,
  children,
  className,
}: PropsGaveta) {
  const ref = useRef<HTMLDivElement>(null);
  const idTitulo = useId();
  const [confirmando, setConfirmando] = useState(false);
  const tentarFechar = () => (alterado ? setConfirmando(true) : aoFechar());

  useFocoPreso(ref, aberto && !confirmando, tentarFechar);
  useTravarRolagem(aberto);
  if (!aberto) return null;

  return createPortal(
    <>
      <div
        aria-hidden
        className="fixed inset-0 z-40 hidden bg-veu animate-fade-entra lg:block"
        onClick={tentarFechar}
      />
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        tabIndex={-1}
        className={cn(
          "fixed inset-0 z-50 flex flex-col bg-painel focus:outline-none",
          "lg:inset-y-0 lg:right-0 lg:left-auto lg:w-full lg:shadow-modal lg:animate-tela-entra",
          largura === "larga" ? "lg:max-w-[520px]" : "lg:max-w-[480px]",
          className,
        )}
      >
        <header
          className={cn(
            "flex h-18 shrink-0 items-center justify-between gap-3 border-b border-fio pt-safe pr-4 pl-6 max-lg:pl-margem",
            compactaNoCelular && "max-lg:h-16 max-lg:pr-3",
            fecharAEsquerdaNoCelular && "max-lg:h-14 max-lg:flex-row-reverse max-lg:justify-end max-lg:gap-1 max-lg:px-2",
          )}
        >
          <h2 id={idTitulo} className="truncate text-h2 text-tinta">
            {titulo}
          </h2>
          {/* 40 visível, alvo de toque 48 */}
          <button
            type="button"
            onClick={tentarFechar}
            aria-label="Fechar"
            className={cn(
              "relative grid size-10 place-items-center rounded-controle text-texto-2 after:absolute after:-inset-1 after:content-[''] hover:bg-recuo",
              compactaNoCelular && "max-lg:size-12 max-lg:after:hidden",
            )}
          >
            <Icones.fechar size={20} aria-hidden />
          </button>
        </header>
        <div
          className={cn(
            "flex-1 overflow-y-auto",
            !semPadding && "space-y-5 p-6 max-lg:px-margem",
            !semPadding && fecharAEsquerdaNoCelular && "max-lg:space-y-4 max-lg:py-5",
          )}
        >
          {children}
        </div>
        {avisoRodape && (
          <p className="shrink-0 px-6 pb-2 text-right text-rotulo text-texto-3 max-lg:px-margem">
            {avisoRodape}
          </p>
        )}
        {rodape && (
          <footer
            className={cn(
              "flex shrink-0 justify-end gap-3 border-t border-fio px-6 pt-4 pb-[calc(1rem+env(safe-area-inset-bottom))] max-lg:px-margem max-lg:[&>*]:flex-1",
              (compactaNoCelular || fecharAEsquerdaNoCelular) && "max-lg:pt-3",
            )}
          >
            {rodape}
          </footer>
        )}
      </div>
      <Confirmar
        aberto={confirmando}
        aoFechar={() => setConfirmando(false)}
        aoConfirmar={() => {
          setConfirmando(false);
          aoFechar();
        }}
        titulo="Descartar alterações?"
        descricao="O que você mudou aqui não foi salvo."
        textoConfirmar="Descartar"
        textoCancelar="Continuar editando"
        perigo
      />
    </>,
    document.body,
  );
}
